// MenuDay.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { Alert, Badge, Button, Form, Table, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faCheckCircle, faTriangleExclamation, faFire } from '@fortawesome/free-solid-svg-icons';
import AutocompleteSelect from '../../../../cabinet/wizplain/components/Autocomplete/AutocompleteSelect';
import { getUserInfo } from '../../../../../services/apiClient';
import useProductAutocomplete from '../../../../../components/ProductAutocomplete/useProductAutocomplete';
import useKitchenMenu from './useKitchenMenu';
import './kitchen-menu.css';

const MEAL_TYPES = ['сніданок', 'обід', 'підвечірок', 'вечеря'];
const AGE_BRACKETS = ['1-3 роки', '3-6 років', '6-10 років'];
const MEAL_ORDER = MEAL_TYPES.reduce((acc, m, i) => ({ ...acc, [m]: i }), {});

const todayStr = () => new Date().toISOString().slice(0, 10);

// Nested API shape (ageGroups[].meals[].items[]) <-> flat rows the UI actually edits.
// Flat is much simpler to add/remove/filter-by-tab; only re-nested right before saving.
const nestedToRows = (ageGroups) => {
  const rows = [];
  (ageGroups || []).forEach(ag => {
    (ag.meals || []).forEach(meal => {
      (meal.items || []).forEach(item => {
        rows.push({ ageGroup: ag.ageGroup, mealType: meal.mealType, dishId: item.dishId, dishName: item.dishName, portions: item.portions });
      });
    });
  });
  return rows;
};

const rowsToNested = (rows) => {
  const byAgeGroup = {};
  rows.forEach(row => {
    if (!row.dishId || !row.portions) return;
    if (!byAgeGroup[row.ageGroup]) byAgeGroup[row.ageGroup] = {};
    if (!byAgeGroup[row.ageGroup][row.mealType]) byAgeGroup[row.ageGroup][row.mealType] = [];
    byAgeGroup[row.ageGroup][row.mealType].push({ dishId: row.dishId, dishName: row.dishName, portions: Number(row.portions) });
  });

  return Object.entries(byAgeGroup).map(([ageGroup, meals]) => ({
    ageGroup,
    meals: Object.entries(meals).map(([mealType, items]) => ({ mealType, items }))
  }));
};

const MenuDay = () => {
  const kindergartenId = useMemo(() => {
    const info = getUserInfo();
    return info && info.privilegedUser ? info.privilegedUser.area : null;
  }, []);

  const [date, setDate] = useState(todayStr());
  const [activeAgeGroup, setActiveAgeGroup] = useState(AGE_BRACKETS[1]);
  const [rows, setRows] = useState([]);
  const [editingRowKey, setEditingRowKey] = useState(null); // dish picker open for this row
  const [confirmCook, setConfirmCook] = useState(false);

  const { day, suggestedPortions, sufficiency, shortfalls, loading, saving, cooking, error, actions } = useKitchenMenu(kindergartenId);
  const dishSearch = useProductAutocomplete({ referenceType: 'dishes' });

  useEffect(() => {
    if (!kindergartenId) return;
    actions.loadDay(date);
    dishSearch.loadInitial();
    setConfirmCook(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kindergartenId, date]);

  useEffect(() => {
    setRows(day && day.ageGroups ? nestedToRows(day.ageGroups) : []);
  }, [day]);

  const isCooked = day && day.status === 'cooked';
  const isSaved = !!day;

  const visibleRows = rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => row.ageGroup === activeAgeGroup)
    .sort((a, b) => MEAL_ORDER[a.row.mealType] - MEAL_ORDER[b.row.mealType]);

  const addRow = () => {
    setRows(prev => [...prev, {
      ageGroup: activeAgeGroup,
      mealType: MEAL_TYPES[0],
      dishId: '', dishName: '',
      portions: suggestedPortions[activeAgeGroup] || ''
    }]);
    setEditingRowKey(rows.length); // new row is always in "picking" mode
  };

  const removeRow = (index) => setRows(prev => prev.filter((_, i) => i !== index));

  const updateRow = (index, patch) => setRows(prev => prev.map((r, i) => i === index ? { ...r, ...patch } : r));

  const pickDish = (index, dishId) => {
    const dish = dishSearch.options.find(d => d._id === dishId);
    updateRow(index, { dishId, dishName: dish ? dish.name : '' });
    setEditingRowKey(null);
  };

  const handleSave = async () => {
    await actions.save(date, rowsToNested(rows));
    await actions.checkSufficiency(date);
  };

  const handleCook = async () => {
    if (!confirmCook) { setConfirmCook(true); return; }
    try {
      await actions.cook(date);
    } catch {
      // shortfalls/error already captured in hook state
    }
    setConfirmCook(false);
  };

  if (!kindergartenId) {
    return <Alert variant="warning">Не вдалося визначити заклад.</Alert>;
  }

  return (
    <div className="card queuetable-table-card kitchen-menu-card">
      <div className="card-body p-0">
        <div className="queuetable-year-bar d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <span className="queuetable-label text-white-50">Меню дня</span>
            <Form.Control
              type="date"
              size="sm"
              style={{ width: '160px' }}
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
            {isCooked && (
              <Badge bg="success">
                <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                Приготовано {new Date(day.cookedAt).toLocaleString('uk-UA')}
              </Badge>
            )}
          </div>
        </div>

        <div className="d-flex kitchen-menu-agegroup-tabs">
          {AGE_BRACKETS.map(b => (
            <button
              key={b}
              className={`kitchen-menu-agegroup-tab ${activeAgeGroup === b ? 'active' : ''}`}
              onClick={() => setActiveAgeGroup(b)}
            >
              {b}
              <span className="text-muted ms-2 small">
                {rows.filter(r => r.ageGroup === b).length} страв
              </span>
            </button>
          ))}
        </div>

        <div className="p-3">
          {error && <Alert variant="danger">{error}</Alert>}

          {suggestedPortions[activeAgeGroup] !== undefined && (
            <Alert variant="light" className="border small py-2">
              Відвідуваність сьогодні ({activeAgeGroup}): <strong>{suggestedPortions[activeAgeGroup] || 0}</strong> дітей —
              кількість порцій для нових позицій підставляється звідси, редагується вручну.
            </Alert>
          )}

          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : (
            <>
              <Table size="sm" responsive className="align-middle mb-2">
                <thead>
                  <tr>
                    <th style={{ width: '140px' }}>Прийом їжі</th>
                    <th style={{ minWidth: '240px' }}>Страва</th>
                    <th style={{ width: '100px' }}>Порцій</th>
                    {!isCooked && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map(({ row, index }) => {
                    const isPicking = editingRowKey === index || !row.dishId;
                    return (
                      <tr key={index}>
                        <td>
                          {isCooked ? row.mealType : (
                            <Form.Select
                              size="sm"
                              value={row.mealType}
                              onChange={(e) => updateRow(index, { mealType: e.target.value })}
                            >
                              {MEAL_TYPES.map(mt => <option key={mt} value={mt}>{mt}</option>)}
                            </Form.Select>
                          )}
                        </td>
                        <td>
                          {isCooked ? row.dishName : isPicking ? (
                            <AutocompleteSelect
                              value={row.dishId}
                              onChange={(id) => pickDish(index, id)}
                              options={dishSearch.options}
                              loading={dishSearch.loading}
                              loadingMore={dishSearch.loadingMore}
                              hasMore={dishSearch.hasMore}
                              onSearch={dishSearch.search}
                              onLoadMore={dishSearch.loadMore}
                              placeholder="Почніть вводити назву страви..."
                            />
                          ) : (
                            <div className="d-flex align-items-center justify-content-between gap-2">
                              <span>{row.dishName}</span>
                              <Button variant="link" size="sm" className="p-0" onClick={() => setEditingRowKey(index)}>
                                Змінити
                              </Button>
                            </div>
                          )}
                        </td>
                        <td>
                          {isCooked ? row.portions : (
                            <Form.Control
                              type="number" min="1" size="sm"
                              value={row.portions}
                              onChange={(e) => updateRow(index, { portions: e.target.value })}
                            />
                          )}
                        </td>
                        {!isCooked && (
                          <td>
                            <Button variant="link" className="text-danger p-0" onClick={() => removeRow(index)}>
                              <FontAwesomeIcon icon={faTrash} />
                            </Button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                  {visibleRows.length === 0 && (
                    <tr><td colSpan={4} className="text-center text-muted py-2">Страви на цю вікову групу не додані</td></tr>
                  )}
                </tbody>
              </Table>

              {!isCooked && (
                <Button variant="outline-secondary" size="sm" className="mb-3" onClick={addRow}>
                  <FontAwesomeIcon icon={faPlus} className="me-1" /> Додати страву
                </Button>
              )}

              {!isCooked && (
                <div className="d-flex gap-2 mb-3">
                  <Button variant="primary" onClick={handleSave} disabled={saving}>
                    {saving && <Spinner animation="border" size="sm" className="me-2" />}
                    Зберегти меню дня
                  </Button>
                  {isSaved && (
                    <Button variant="outline-secondary" onClick={() => actions.checkSufficiency(date)}>
                      Перевірити достатність продуктів
                    </Button>
                  )}
                </div>
              )}

              {sufficiency && sufficiency.length > 0 && (
                <div className="mb-3">
                  <h6 className="mb-2">Достатність продуктів на складі (по всьому дню)</h6>
                  <Table size="sm" responsive>
                    <thead>
                      <tr>
                        <th>Продукт</th>
                        <th>Потрібно</th>
                        <th>Є на складі</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sufficiency.map(row => (
                        <tr key={row.baseProductId} className={row.sufficient ? '' : 'table-danger'}>
                          <td>{row.baseProductName}</td>
                          <td>{row.required} {row.unit}</td>
                          <td>{row.available} {row.unit}</td>
                          <td>
                            {row.sufficient ? (
                              <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
                            ) : (
                              <FontAwesomeIcon icon={faTriangleExclamation} className="text-danger" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              {shortfalls && shortfalls.length > 0 && (
                <Alert variant="danger">
                  <strong>Недостатньо продуктів для приготування:</strong>
                  <ul className="mb-0 mt-1">
                    {shortfalls.map(s => (
                      <li key={s.baseProductId}>
                        {s.baseProductName}: потрібно {s.required}, є {s.available}
                      </li>
                    ))}
                  </ul>
                </Alert>
              )}

              {!isCooked && isSaved && (
                <div>
                  <Button variant={confirmCook ? 'danger' : 'success'} onClick={handleCook} disabled={cooking}>
                    {cooking && <Spinner animation="border" size="sm" className="me-2" />}
                    <FontAwesomeIcon icon={faFire} className="me-2" />
                    {confirmCook ? 'Підтвердити приготування і списати зі складу' : 'Приготовано'}
                  </Button>
                  {confirmCook && (
                    <Button variant="link" size="sm" onClick={() => setConfirmCook(false)}>Скасувати</Button>
                  )}
                  {confirmCook && (
                    <p className="text-muted small mt-1 mb-0">
                      Після підтвердження продукти буде списано зі складу (по всіх вікових групах і прийомах їжі),
                      а меню дня більше не можна буде редагувати.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MenuDay;